import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { applicationApi } from '../api/application';
import { Application, ApplicationUpdateRequest } from '../types/application';

interface ApplicationSettingsPageProps {
  application: Application;
}

const ApplicationSettingsPage: React.FC<ApplicationSettingsPageProps> = ({ application }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSubsclightApiKey, setShowSubsclightApiKey] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ApplicationUpdateRequest>({
    defaultValues: {
      name: application.name,
      description: application.description,
      version: application.version,
      subsclight_api_key: '', // セキュリティのため空にしておく
      subsclight_site: application.subsclight_site,
    },
  });

  const updateMutation = useMutation({
    mutationFn: applicationApi.updateMyApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myApplication'] });
      setIsEditing(false);
    },
  });

  const onSubmit = async (data: ApplicationUpdateRequest) => {
    try {
      // APIキーが空の場合は更新対象から除外
      const updateData = { ...data };
      if (!updateData.subsclight_api_key) {
        delete updateData.subsclight_api_key;
      }
      await updateMutation.mutateAsync(updateData);
    } catch (error) {
      console.error('アプリケーションの更新に失敗しました:', error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    reset({
      name: application.name,
      description: application.description,
      version: application.version,
      subsclight_api_key: '',
      subsclight_site: application.subsclight_site,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const maskApiKey = (key: string) => {
    if (!key || key.length < 8) return key;
    return key.substring(0, 4) + '*'.repeat(key.length - 8) + key.substring(key.length - 4);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center">
            <SettingsIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                マイアプリケーション設定
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                アプリケーションの詳細設定とサブスクライト連携を管理
              </Typography>
            </Box>
          </Box>
          {!isEditing && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              設定を編集
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* 基本情報カード */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  基本情報
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    アプリケーションID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {application.application_id}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    内部APIキー
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                      {showApiKey ? application.api_key : maskApiKey(application.api_key)}
                    </Typography>
                    <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)}>
                      {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    作成日時
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(application.created_at), 'yyyy年M月d日 HH:mm', { locale: ja })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    更新日時
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(application.updated_at), 'yyyy年M月d日 HH:mm', { locale: ja })}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* サブスクライト連携情報カード */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  サブスクライト連携
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    サイト識別子
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {application.subsclight_site}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    サブスクライトAPIキー
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                      {showSubsclightApiKey ? application.subsclight_api_key : maskApiKey(application.subsclight_api_key)}
                    </Typography>
                    <IconButton size="small" onClick={() => setShowSubsclightApiKey(!showSubsclightApiKey)}>
                      {showSubsclightApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 編集フォーム */}
          {isEditing && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  設定の編集
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="アプリケーション名"
                        {...register('name', {
                          required: 'アプリケーション名は必須です',
                          minLength: {
                            value: 2,
                            message: 'アプリケーション名は2文字以上で入力してください',
                          },
                          maxLength: {
                            value: 100,
                            message: 'アプリケーション名は100文字以下で入力してください',
                          },
                        })}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="バージョン"
                        {...register('version', {
                          required: 'バージョンは必須です',
                          pattern: {
                            value: /^\d+\.\d+\.\d+$/,
                            message: 'バージョンは「x.y.z」の形式で入力してください（例：1.0.0）',
                          },
                        })}
                        error={!!errors.version}
                        helperText={errors.version?.message}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="説明"
                        multiline
                        rows={3}
                        {...register('description', {
                          required: '説明は必須です',
                          maxLength: {
                            value: 1000,
                            message: '説明は1000文字以下で入力してください',
                          },
                        })}
                        error={!!errors.description}
                        helperText={errors.description?.message}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        サブスクライト連携設定
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="サブスクライトサイト識別子"
                        {...register('subsclight_site', {
                          required: 'サブスクライトサイト識別子は必須です',
                          pattern: {
                            value: /^[a-zA-Z0-9\-_]+$/,
                            message: 'サイト識別子は英数字、ハイフン、アンダースコアのみ使用できます',
                          },
                        })}
                        error={!!errors.subsclight_site}
                        helperText={errors.subsclight_site?.message}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="サブスクライトAPIキー"
                        type="password"
                        {...register('subsclight_api_key', {
                          minLength: {
                            value: 10,
                            message: 'APIキーは10文字以上で入力してください',
                          },
                        })}
                        error={!!errors.subsclight_api_key}
                        helperText={errors.subsclight_api_key?.message || '変更する場合のみ入力してください'}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancel}
                          disabled={updateMutation.isPending}
                        >
                          キャンセル
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending ? (
                            <CircularProgress size={24} />
                          ) : (
                            '設定を保存'
                          )}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          )}
        </Grid>

        {updateMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            設定の更新に失敗しました。入力内容を確認してもう一度お試しください。
          </Alert>
        )}

        {updateMutation.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            設定が正常に更新されました。
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default ApplicationSettingsPage;